import assert from 'assert';
import {useCallback, useMemo, useRef} from 'react';
import {createSelector} from 'reselect';

import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {getErrorOrWarningFromCell} from 'notebook/tables/errors/CellErrorGetters';
import {EvaluatedCellError} from 'notebook/tables/excess/Evaluator';
import spreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';
import {HydratedTableContext} from 'notebook/tables/store/contexts/HydratedTableContext';
import {TableNoteContext} from 'notebook/tables/store/contexts/TableNoteContext';
import {useTableActions} from 'notebook/tables/store/hooks/useTableActions';
import {getEvaluatedCell, getEvaluatedRow} from 'notebook/tables/store/selectors/TableSelectors';
import {Cell, TableFunction} from 'notebook/tables/TableConstants';
import {Note} from 'notebook/util/NoteUtilConstants';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import {Optional} from 'util/Types';

interface CrossStepFormulaBoxModel {
  noteCid: string;
  rowId?: string;
  cellError: Optional<EvaluatedCellError>;
  lastIndex: TableIndex | null;
}

export function useCrossStepFormulaBox(index: TableIndex): CrossStepFormulaBoxModel & {
  cid: string;
  updateCell: (updatedCell: Partial<Cell>) => Promise<void>;
  onFunctionClick: (tableFunction: TableFunction) => void;
} {
  const {getNote, useNoteSelector} = TableNoteContext.useContext();
  const {tableName, selectTable} = HydratedTableContext.useContext();
  const {updateCell, setActiveFormulaText} = useTableActions();

  const rowIndex = index[0];
  const selectEvaluatedRow = useMemo(() => getEvaluatedRow(tableName, rowIndex), [tableName, rowIndex]);

  const selectCrossStepFormulaBoxModel = useMemo(
    () =>
      createSelector(
        [
          selectTable,
          (note: Note) => note.cid,
          (note: Note) => getEvaluatedCell(tableName, index[0], index[1])(note),
        ],
        (table, noteCid, evaluatedCell): CrossStepFormulaBoxModel => {
          if (!table) {
            return {
              noteCid,
              cellError: null,
              lastIndex: null,
            };
          }
          const cell = table.table[index[0]][index[1]];
          return {
            noteCid,
            rowId: table.rowData?.[index[0]].rowId ?? undefined,
            cellError: evaluatedCell ? getErrorOrWarningFromCell(cell, evaluatedCell) : cell.error,
            lastIndex: [table.table.length - 1, table.table[0].length - 1] satisfies TableIndex,
          };
        }
      ),
    [index, selectTable, tableName]
  );

  const crossStepFormulaBoxModel = useNoteSelector(selectCrossStepFormulaBoxModel);
  const currentSelectionCid = useStoreSelector(spreadsheetStore, (store) => store.selection?.cid);

  const cidRef = useRef<string | undefined>(undefined);
  if (!cidRef.current && currentSelectionCid) {
    cidRef.current = currentSelectionCid;
  }

  const crossStepFormulaBoxCid = cidRef.current;

  assert(crossStepFormulaBoxCid !== undefined, 'No active cell found when using CrossStepFormulaBox');

  const onFunctionClick = useCallback(
    async ({name, asyncOnClick}: TableFunction): Promise<void> => {
      if (asyncOnClick == null) {
        // Set the position such that the cursor is between the brackets
        const pos = 1 + name.length + 1;
        setActiveFormulaText(index, `=${name}()`, pos, crossStepFormulaBoxCid);
      } else {
        const note = getNote();
        const table = selectTable(note);
        assert(table != null, `Table not found for ${tableName}`);
        const evaluatedRow = selectEvaluatedRow(note);
        const rowData = table.rowData?.[index[0]] ?? null;
        const result = await asyncOnClick({
          rowData,
          evaluatedRow,
          onUpdateSelectedCell: (cellData: Partial<Cell> & {text: string}) => updateCell(index, cellData),
        });
        if (result) {
          setActiveFormulaText(index, `=${result.formula}()`, result.cursorPosition, crossStepFormulaBoxCid);
        }
      }
    },
    [
      crossStepFormulaBoxCid,
      selectEvaluatedRow,
      getNote,
      index,
      selectTable,
      setActiveFormulaText,
      tableName,
      updateCell,
    ]
  );

  const updateCellMemozied = useCallback(
    (updatedCell: Partial<Cell> & {text: string}): Promise<void> => updateCell(index, updatedCell),
    [index, updateCell]
  );

  return {
    ...crossStepFormulaBoxModel,
    cid: crossStepFormulaBoxCid,
    updateCell: updateCellMemozied,
    onFunctionClick,
  };
}
